import { useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import logoMark from "@/assets/logo-mark-3d.png";

/**
 * AfriTask AI brand mark — static 3D extruded 'A' monogram with a subtle
 * idle Y-axis sway (like a physical object breathing).
 *
 * The mark renders directly on the surrounding surface — no background pill,
 * border, ring, shadow, or padding wrapper. The PNG has baked-in depth /
 * shadow already.
 *
 * TODO: variant="mono" currently falls back to the same coloured 3D PNG.
 *       When we need it on dark bands we should ship a dedicated monochrome
 *       asset — do NOT CSS-tint this PNG, it will look muddy.
 */
export function Logo({ size = "md", withText = true, variant = "dark", to }) {
  const location = useLocation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  // Outer wrapper defines the reserved slot in the header/nav.
  // Inner mark uses scale-110 to fill more of the slot (PNG has transparent
  // padding around the 'A' glyph, so without scale the glyph reads small).
  const sizes = {
    sm: "h-7 w-7",
    md: "h-9 w-9",
    lg: "h-10 w-10",
  };
  const textSizes = { sm: "text-sm", md: "text-base", lg: "text-lg" };

  const textCls = variant === "dark" ? "text-[#111827]" : "text-white";

  const isAuthed =
    typeof window !== "undefined" &&
    !!localStorage.getItem("afritask.session_token");
  const inApp = location.pathname.startsWith("/app");
  const destination = to || (inApp || isAuthed ? "/app/dashboard" : "/");

  const swayAnimation = reduce
    ? {}
    : { rotateY: [0, 6, 0, -6, 0] };
  const swayTransition = reduce
    ? {}
    : { duration: 8, ease: "easeInOut", repeat: Infinity };

  return (
    <button
      type="button"
      onClick={() => navigate(destination)}
      className="inline-flex items-center gap-2 focus:outline-none"
      data-testid="brand-logo"
    >
      {/* Perspective parent — makes the rotateY read as real 3D, not a flat stretch */}
      <span
        className={`${sizes[size]} inline-flex shrink-0 items-center justify-center`}
        style={{ perspective: "600px" }}
      >
        <motion.div
          className="h-full w-full"
          animate={swayAnimation}
          transition={swayTransition}
          style={{
            transformStyle: "preserve-3d",
            transformOrigin: "center",
          }}
        >
          <img
            src={logoMark}
            alt="AfriTask AI"
            loading="eager"
            decoding="async"
            className="h-full w-full scale-125 object-contain"
            style={{ imageRendering: "auto" }}
          />
        </motion.div>
      </span>
      {withText && (
        <span className={`font-semibold tracking-tight ${textSizes[size]} ${textCls}`}>
          AfriTask <span style={{ color: "#B45309" }}>AI</span>
        </span>
      )}
    </button>
  );
}
